import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { CustomDatePicker } from "../../../components/Custom/CustomDatePicker";
import { FilialSelect, FilialDivisionSelect2 } from "../../../components/Select/domainSelects";
import { toLocalCalendarDateString } from "../../../functions/dateHelpers";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const toNullableNumber = (v) => {
    if (v === "" || v === undefined || v === null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const normalizeAddressToLines = (address) => {
    if (address == null) return [""];

    // If backend stored it as string, try parse JSON; otherwise keep as single line
    if (typeof address === "string") {
        const t = address.trim();
        if (!t) return [""];
        if (t.startsWith("[") || t.startsWith("{")) {
            try {
                return normalizeAddressToLines(JSON.parse(t));
            } catch (e) {
                // fall through
            }
        }
        return [address];
    }

    if (Array.isArray(address)) {
        const lines = address.map((x) => (x == null ? "" : String(x)));
        return lines.length ? lines : [""];
    }

    // For object/number/etc. keep as one JSON-ish line so we don't lose data
    try {
        return [JSON.stringify(address)];
    } catch (e) {
        return [String(address)];
    }
};

const buildAddressFromLines = (lines) => {
    const cleaned = (Array.isArray(lines) ? lines : [])
        .map((l) => String(l ?? "").trim())
        .filter(Boolean);
    return cleaned.length ? cleaned : null;
};

const AccreditationMarkIndicatorCreateModal = ({
    open,
    onClose,
    onSubmit,
    initialData,
    mode = "create", // create | edit | details
}) => {
    const isReadOnly = mode === "details";

    const emptyForm = useMemo(
        () => ({
            id: null,
            filialId: "",
            filialDivisionId: "",
            certificateNumber: "",
            name: "",
            addressLines: [""],
            issueDate: null,
            validityPeriod: null,
            isIrrelevant: false,
            state: 1,
        }),
        []
    );

    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFilialChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            filialId: value,
            filialDivisionId: prev.filialDivisionId && Number(prev.filialId) !== Number(value) ? "" : prev.filialDivisionId,
        }));
    };

    const handleFilialDivisionChange = (value, filialDivision) => {
        setFormData((prev) => ({
            ...prev,
            filialDivisionId: value,
            filialId:
                filialDivision != null &&
                Number(filialDivision.filialId) !== Number(prev.filialId)
                    ? filialDivision.filialId
                    : prev.filialId,
        }));
    };

    const handleAddressLineChange = (idx, value) => {
        setFormData((prev) => {
            const next = Array.isArray(prev.addressLines) ? [...prev.addressLines] : [""];
            next[idx] = value;
            return { ...prev, addressLines: next };
        });
    };

    const addAddressLine = () => {
        setFormData((prev) => ({
            ...prev,
            addressLines: [...(Array.isArray(prev.addressLines) ? prev.addressLines : [""]), ""],
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!onSubmit) {
            onClose();
            return;
        }

        setSaving(true);
        const { addressLines, ...rest } = formData;
        Promise.resolve(
            onSubmit({
                ...rest,
                id: toNullableNumber(rest.id),
                filialId: toNullableNumber(rest.filialId),
                filialDivisionId: toNullableNumber(rest.filialDivisionId),
                address: buildAddressFromLines(addressLines),
                issueDate: toLocalCalendarDateString(rest.issueDate),
                validityPeriod: toLocalCalendarDateString(rest.validityPeriod),
            })
        )
            .then(() => handleClose())
            .catch((err) => alert(err))
            .finally(() => setSaving(false));
    };

    const handleClose = () => {
        setFormData(emptyForm);
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        if (!initialData) {
            setFormData(emptyForm);
            return;
        }
        setFormData((prev) => ({
            ...prev,
            id: initialData.id ?? null,
            filialId: initialData.filialId ?? "",
            filialDivisionId: initialData.filialDivisionId ?? "",
            certificateNumber: initialData.certificateNumber ?? "",
            name: initialData.name ?? "",
            addressLines: normalizeAddressToLines(initialData.address),
            issueDate: initialData.issueDate ? new Date(initialData.issueDate) : null,
            validityPeriod: initialData.validityPeriod
                ? new Date(initialData.validityPeriod)
                : null,
            isIrrelevant: Boolean(initialData.isIrrelevant),
            state: initialData.state ?? 1,
        }));
    }, [open, initialData, emptyForm]);

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
                    {mode === "edit"
                        ? "Редактирование индикатора аккредитации"
                        : mode === "details"
                        ? "Просмотр индикатора аккредитации"
                        : "Индикатор по аккредитации"}
                </Typography>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FilialSelect
                                label="Филиал"
                                fullWidth
                                value={formData.filialId}
                                onChange={handleFilialChange}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FilialDivisionSelect2
                                label="Районное отделение"
                                fullWidth
                                value={formData.filialDivisionId}
                                filialId={formData.filialId}
                                onChange={handleFilialDivisionChange}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Номер аттестата аккредитации"
                                value={formData.certificateNumber}
                                onChange={(e) => handleChange("certificateNumber", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="normal"
                                fullWidth
                                label="Название"
                                value={formData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                Адрес
                            </Typography>
                            <Grid container spacing={1}>
                                {(Array.isArray(formData.addressLines) ? formData.addressLines : [""]).map((line, idx) => (
                                    <Grid item xs={12} key={idx}>
                                        <TextField
                                            fullWidth
                                            label={`Строка ${idx + 1}`}
                                            value={line}
                                            onChange={(e) => handleAddressLineChange(idx, e.target.value)}
                                            disabled={isReadOnly}
                                        />
                                    </Grid>
                                ))}
                                {!isReadOnly && (
                                    <Grid item xs={12}>
                                        <Button variant="outlined" onClick={addAddressLine}>
                                            +
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <CustomDatePicker
                                label="Дата выдачи аттестата аккредитации"
                                value={formData.issueDate}
                                onChange={(value) => handleChange("issueDate", value)}
                                autoFormatDDMMYYYY
                                fullWidth
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomDatePicker
                                label="Срок действия аккредитации"
                                value={formData.validityPeriod}
                                onChange={(value) => handleChange("validityPeriod", value)}
                                autoFormatDDMMYYYY
                                fullWidth
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isIrrelevant}
                                        onChange={(e) => handleChange("isIrrelevant", e.target.checked)}
                                        disabled={isReadOnly}
                                    />
                                }
                                label="Неактуальный"
                            />
                        </Grid>
                        {mode !== "details" && (
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 2, mb: 2 }}
                                    disabled={saving}
                                >
                                    Сохранить
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>
        </Modal>
    );
};

export default AccreditationMarkIndicatorCreateModal;

